<script setup>
import { useTemplateRef } from 'vue';
import { useValidation, useFormContext } from '/app/composables/form';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    name: { type: String, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    fieldName: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    forceError: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    modelValue: {
        type: String,
        default: ''
    },
    size: {
        type: String,
        default: 'medium'
    },
    startWith: {
        type: String
    },
    rows: {
        type: [Number, String],
        default: 3
    },
    maxlength: {
        type: [Number, String],
        default: 1000
    },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const form = useFormContext();
const { invalid, error, attrs, clearErrors, setError, focus, target } = useValidation(props);

const input = useTemplateRef('input');

function update({ target: t }) {
    clearErrors();

    if (props.startWith && t.textLength < props.startWith.length) {
        emit('update:modelValue', props.startWith + ' ');
    } else {
        emit('update:modelValue', t.value);
    }
}

defineExpose({ input, clearErrors, setError, focus, target });
</script>

<template>
    <div class="form-group__helper">
        <label v-if="label" :for="name" class="form-group__label" style="display: block;">{{ label }}</label>
        <span v-if="hint" class="form-group__hint" style="display: block;">{{ hint }}</span>
        <textarea
            v-bind="attrs"
            class="form-group__input is-textarea border border-dust-300 rounded-md"
            ref="input"
            :class="[`is-${size}`, { 'is-error': invalid || forceError}, { '!text-red-400 !border-red-500 !placeholder-red-300': invalid || forceError, 'bg-dust-100': disabled }]"
            :id="name"
            :name="name"
            :value="modelValue"
            :disabled="form.busy || disabled"
            :rows="rows"
            :maxlength="maxlength"
            @input="update"
            @keydown="form.errors.clear(name)"
            @blur="e => $emit('blur', e)"
        ></textarea>
        <slot name="error" :invalid="invalid" :error="error">
            <span v-if="invalid && error" class="form-group__error">{{ error }}</span>
        </slot>
    </div>
</template>
